// Polyurethane calculation helpers. All educational.

// Equivalent weight of a polyol from OH number (mg KOH/g)
export function polyolEquivalentWeight(ohNumber) {
  const oh = Number(ohNumber);
  if (!oh || oh <= 0) return null;
  return 56100 / oh;
}

// Equivalent weight of an isocyanate from % NCO
export function isocyanateEquivalentWeight(ncoPercent) {
  const nco = Number(ncoPercent);
  if (!nco || nco <= 0) return null;
  return 4200 / nco;
}

// NCO/OH ratio from weights + values
export function ncoOhRatio({ polyolWeight, ohValue, isoWeight, ncoPercent }) {
  const pw = Number(polyolWeight);
  const oh = Number(ohValue);
  const iw = Number(isoWeight);
  const nco = Number(ncoPercent);
  if (!pw || !oh || !iw || !nco) return null;

  const ohEqWeight = 56100 / oh; // g/eq
  const ncoEqWeight = 4200 / nco; // g/eq
  const ohEquivalents = pw / ohEqWeight;
  const ncoEquivalents = iw / ncoEqWeight;
  const ratio = ncoEquivalents / ohEquivalents;
  return {
    ohEqWeight,
    ncoEqWeight,
    ohEquivalents,
    ncoEquivalents,
    ratio,
    index: ratio * 100,
  };
}

// Isocyanate index -> required isocyanate weight
// Given total OH equivalents in formulation and desired index
export function isocyanateRequirement({ polyolWeight, ohValue, waterWeight, ncoPercent, index }) {
  const pw = Number(polyolWeight);
  const oh = Number(ohValue);
  const water = Number(waterWeight) || 0;
  const nco = Number(ncoPercent);
  const idx = Number(index);
  if (!pw || !oh || !nco || !idx) return null;

  const ohEqWeight = 56100 / oh;
  const polyolEq = pw / ohEqWeight;
  // water: molar mass 18, 2 reactive H -> equivalent weight 9 g/eq
  const waterEq = water / 9;
  const totalActiveH = polyolEq + waterEq;

  const ncoEqWeight = 4200 / nco;
  const requiredNcoEq = totalActiveH * (idx / 100);
  const requiredIsoWeight = requiredNcoEq * ncoEqWeight;

  return {
    ohEqWeight,
    polyolEq,
    waterEq,
    totalActiveH,
    ncoEqWeight,
    requiredNcoEq,
    requiredIsoWeight,
  };
}

export function fmt(n, digits = 2) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

// PU Mix Ratio: parts by weight A:B -> amounts for a target batch weight
export function puMixRatio({ partsA, partsB, batchWeight }) {
  const a = Number(partsA);
  const b = Number(partsB);
  const batch = Number(batchWeight);
  if (!a || !b || a <= 0 || b <= 0) return null;
  const total = a + b;
  const ratioA = a / total;
  const ratioB = b / total;
  const res = { ratioText: `${(a / b).toFixed(3)} : 1`, percentA: ratioA * 100, percentB: ratioB * 100 };
  if (batch && batch > 0) {
    res.weightA = ratioA * batch;
    res.weightB = ratioB * batch;
  }
  return res;
}

// Water / NCO demand: extra isocyanate consumed by water in a foam formulation
export function waterNcoDemand({ waterWeight, ncoPercent }) {
  const water = Number(waterWeight);
  const nco = Number(ncoPercent);
  if (!water || water <= 0) return null;
  // water eq weight ~9 g/eq; each water eq consumes 1 NCO eq
  const waterEq = water / 9;
  const ncoEqConsumed = waterEq; // 1:1
  const co2Moles = water / 18; // mol CO2 per water (1:1)
  const res = { waterEq, ncoEqConsumed, co2Moles };
  if (nco && nco > 0) {
    const ncoEqWeight = 4200 / nco;
    res.extraIsoWeight = ncoEqConsumed * ncoEqWeight;
    res.ncoEqWeight = ncoEqWeight;
  }
  return res;
}

// Multi-polyol blend: combine several reactive components into a blended OH number & equivalents
// components: [{name, weight, oh, water}]  (water optional, contributes as active hydrogen)
export function multiPolyolBlend(components = []) {
  const rows = [];
  let totalWeight = 0;
  let totalEq = 0;
  for (const c of components) {
    const w = Number(c.weight);
    const oh = Number(c.oh);
    const water = Number(c.water) || 0;
    if (!w || w <= 0) continue;
    let eq = 0;
    if (oh && oh > 0) eq += w / (56100 / oh);
    if (water > 0) eq += (w * (water / 100)) / 9; // water content as % of component weight
    rows.push({ name: c.name || "—", weight: w, oh: oh || 0, eq });
    totalWeight += w;
    totalEq += eq;
  }
  if (totalWeight <= 0 || totalEq <= 0) return null;
  const blendedEqWeight = totalWeight / totalEq;
  const blendedOh = 56100 / blendedEqWeight;
  return { rows, totalWeight, totalEq, blendedEqWeight, blendedOh };
}

